import React, {memo, useState} from "react";
import __ from "lodash";
import {
  ComposableMap,
  Geographies,
  Geography,
  Line,
} from "react-simple-maps";
import { getPointsEnRoute } from "../../functions/Geo";
import {fetch as fetchAirport} from "../../services/airports/useAirports";
import {fetch as fetchJetPlan} from "../../services/jeppesen/useFlightJetPlans";
import {AircraftMarker, AirportMarker} from "../Map";
import {useQueryClient} from "react-query";

const geoUrl =
  // "https://raw.githubusercontent.com/zcreativelabs/react-simple-maps/master/topojson-maps/world-110m.json";
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const getAirportData = async (airportCode, queryClient) => {
  // const apiSitePrefix = getApiSitePrefix();

  const airportData = await fetchAirport({queryClient, extraKeys:[airportCode], useCache: true}); // axios.get(`${apiSitePrefix}/airports/list/${airportCode}`);
  return airportData[0];
}
  
const getJetplanRoute = async (uuid, queryClient) => {
  const planData = await fetchJetPlan({queryClient, extraKeys:[uuid, 'FULL'], useCache: true}); 
  let navPoints = [];
  try {
    navPoints = JSON.parse(planData?.navPoints||[]);
  } catch (err) {}
  return (__.filter(navPoints, {type: "main"}));
}
  
const buildRoute = async (origin, destination, tripRoute, _onwardRoutes, queryClient, uuid) => {
  const onwardRoutes = {truck:[], flight:[], ..._onwardRoutes};
  const route = [];
  const planRoute = await getJetplanRoute(uuid, queryClient);
  const addAirportToRoute = async (airportCode, transport) => {
    const airportData = await getAirportData(airportCode, queryClient);
    route.push({airportCode, transport, long: airportData.longitude, lat: airportData.latitude});
  }
  const truckRoutes = onwardRoutes['truck'];
  const flightRoutes = onwardRoutes['flight'];
  const truckDestinations = (truckRoutes||[]).map((route)=>route.split('-')[1]);
  const flightDestinations = (flightRoutes||[]).map((route)=>route.split('-')[1]);
  const tripAirports = (tripRoute||`${origin}-${destination}`).split('-');
  const originPos = tripAirports.indexOf(origin);
  const routeFromOrigin = tripAirports.slice(originPos); 
  const destinationPos = routeFromOrigin.indexOf(destination);
  if (destinationPos < 0) {
    if (flightDestinations.indexOf(destination) >= 0){
      const flightOrigin = truckRoutes[truckDestinations.indexOf(destination)].split('-')[0];              
      const flightOriginPos = tripAirports.indexOf(flightOrigin);
      const trimmedRoute = routeFromOrigin.slice(0, flightOriginPos + 1);
      for (let i = 0; i < trimmedRoute.length; i++) {
        await addAirportToRoute(trimmedRoute[i], "PRIMARY");
      }  
      await addAirportToRoute(destination, "FLIGHT");
    } else {
      if (truckDestinations.indexOf(destination) >= 0){
        const truckOrigin = truckRoutes[truckDestinations.indexOf(destination)].split('-')[0];  
        const truckOriginPos = tripAirports.indexOf(truckOrigin);
        if (truckOriginPos < 0) {
          const flightOrigin = truckRoutes[truckDestinations.indexOf(truckOrigin)].split('-')[0];              
          const flightOriginPos = tripAirports.indexOf(flightOrigin);
          const trimmedRoute = routeFromOrigin.slice(0, flightOriginPos + 1);
          for (let i = 0; i < trimmedRoute.length; i++) {
            await addAirportToRoute(trimmedRoute[i], "PRIMARY");
          }  
          await addAirportToRoute(truckOrigin, "FLIGHT");
          await addAirportToRoute(destination, "TRUCK");
        } else {
          const trimmedRoute = routeFromOrigin.slice(0, truckOriginPos + 1);
          if (trimmedRoute.length === 2 && planRoute) {
            await addAirportToRoute(trimmedRoute[0], "PRIMARY");
            for (let i = 0; i < planRoute.length; i++) {
              route.push({transport: "PRIMARY", long: planRoute[i].long, lat: planRoute[i].lat});
            }    
            await addAirportToRoute(trimmedRoute[1], "PRIMARY");
          } else {
            for (let i = 0; i < trimmedRoute.length; i++) {
              await addAirportToRoute(trimmedRoute[i], "PRIMARY");
            }    
          }
          await addAirportToRoute(destination, "TRUCK");
        }
      }  
    }
  } else {
    const trimmedRoute = routeFromOrigin.slice(0, destinationPos + 1);
    if (trimmedRoute.length === 2 && planRoute) {
      await addAirportToRoute(trimmedRoute[0], "PRIMARY");
      for (let i = 0; i < planRoute.length; i++) {
        route.push({transport: "PRIMARY", long: planRoute[i].long, lat: planRoute[i].lat});
      }    
      await addAirportToRoute(trimmedRoute[1], "PRIMARY");
    } else {
      for (let i = 0; i < trimmedRoute.length; i++) {
        await addAirportToRoute(trimmedRoute[i], "PRIMARY");
      }    
    }
  }

  return route;
}

const RouteMap = (props) => {
  const {
    record, 
    width, 
    height
  } = {
    width: 1000,
    height: 540,
    ...props
  };

  const queryClient = useQueryClient();

  const [state, setState] = useState({
    markers: [],
    route: [],
    bookingUUid: null,
    flownRoute: [],
    aircraft: {coordinates: [], bearing: 0, angle: 0},
    projectionConfig: {
      scale: 1, 
      rotation: [0,0,0]
    },
    record: null
  });

  if (JSON.stringify(state.record) !== JSON.stringify(record)) {
    console.log(JSON.stringify(state.record), JSON.stringify(record));
    buildRoute(record.origin, record.destination, record.tripRoute, record.onwardRoutes, queryClient, record.uuid).then(
      (route)=> {
        if (!route || route.length < 1) {
          setState({...state, markers: [], route: [], flownRoute: [], aircraft: {coordinates: [], bearing: 0, angle: 0}, record});
          return;
        };
        const markers = [];
        let smallestLat = null;
        let smallestLong = null;
        let biggestLat = null;
        let biggestLong = null;
        for (let i = 0; i < route.length; i++ ){
          markers.push( { name: route[i].airportCode, coordinates: [route[i].long, route[i].lat] });
          if (smallestLat === null || route[i].lat < smallestLat) smallestLat = route[i].lat;
          if (biggestLat === null || route[i].lat > biggestLat) biggestLat = route[i].lat;
          if (smallestLong === null || route[i].long < smallestLong) smallestLong = route[i].long;
          if (biggestLong === null || route[i].long > biggestLong) biggestLong = route[i].long;
        }
        const routePoints = [];
        let flownPoints = [];
        const points = [];
        let aircraft = {coordinates:[route[0].long, route[0].lat], bearing: 0, angle: 0};
        for (let i = 1; i < route.length; i++ ){
          const aircraftPoints = getPointsEnRoute(route[i-1], route[i], 5000);
          aircraftPoints.forEach((data)=> points.push(data));
        }
        routePoints.push(points.map((data)=> [data.long, data.lat]));
        flownPoints = [routePoints[0].slice(0, Math.floor((routePoints[0].length - 1)/(100/record.progress)))];
        if (Array.isArray(flownPoints) && flownPoints.length > 0 && Array.isArray(flownPoints[0]) && flownPoints[0].length > 0) {
          const lastPointFlown = points[flownPoints[0].length - 1];
          aircraft = {coordinates: [lastPointFlown.long, lastPointFlown.lat], ...lastPointFlown};
        } else {
          aircraft = {coordinates: [points[0].long, points[0].lat], ...points[0]};
        }
        // const smallestHeight = smallestLat>=0?90 - smallestLat: 180 + smallestLat;
        // const biggestHeight = biggestLat>=0?90 - biggestLat: 180 + biggestLat;
        // const smallestWidth = smallestLong>=0?180 - smallestLong: 360 + smallestLong;
        // const biggestWidth = biggestLong>=0?180 - biggestLong: 360 + biggestLong;
        
        const widthPercent = (Math.abs(biggestLong - smallestLong));
        const heightPercent = Math.max(9, (Math.abs(biggestLat - smallestLat) * (width/height) * 1.7));
        
        const projectionConfig = {
          scale: (width * 0.9 / (Math.max(widthPercent, heightPercent)/180 * Math.PI)),// (Math.max(widthPercent, heightPercent))),
          center: [(biggestLong + smallestLong)/2, (biggestLat + smallestLat)/2]
        }
        setState({...state, markers, route: routePoints, flownRoute: flownPoints, record, aircraft, projectionConfig});
        if (props.routeListener) {
          props.routeListener(route);
        }
      }
    );    
  }
  if (!state.route || !Array.isArray(state.route) || !state.length < 1) return (<></>);

  const map = (
    <ComposableMap
      width={width}
      height={height}
      projection="geoMercator"
      projectionConfig={state.projectionConfig}
    >
      <Geographies geography={geoUrl}>
        {({ geographies }) =>
          geographies
            .map(geo => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#DADADA"
                stroke="#B0B0B0"
              />
            ))
        }
      </Geographies>
      {(state.route||[]).map((points) => (
        <Line
          coordinates={points}
          stroke="#000000"
          strokeWidth={1}
          // strokeDasharray={[1, 1]}
        />
      ))}
      {(state.flownRoute||[]).map((points) => (
        <Line
          coordinates={points}
          stroke="#ff0000"
          strokeWidth={2}
        />
      ))}
      {state.markers.map(({ name, coordinates, markerOffset }) => (
        <AirportMarker name={name} coordinates={coordinates}/>
      ))}
      {state.aircraft ?
        <AircraftMarker coordinates={state.aircraft.coordinates} bearing={state.aircraft.bearing} callSign={state?.record?.callSign}/>
      : <></>
      }
    </ComposableMap>
  );
  return map;
};

export default memo(RouteMap)